import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { ApiError } from '../../../services/api';
import {
  createBillingContact,
  deactivateBillingContact,
  getBillingContacts,
  updateBillingContact,
} from '../../../services/admin-billing';
import type { BillingContact } from '../../../types/adminBilling';
import { BillingContactModal, type BillingContactDraft } from './BillingContactModal';
import { AdminPanel } from '../AdminPage';

interface BillingContactsPanelProps {
  organizationId: string;
  /** Called after any change, so the page's summary contact counts follow. */
  onChanged: () => void;
}

const describe = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

/**
 * Who invoices and reminders are sent to.
 *
 * **These are the accounts payable addresses, not the coaches'.** The panel says so in as many
 * words, because the rest of this product is about coaches and an admin typing the head coach's
 * address here would send a past-due notice to the person least able to act on it.
 *
 * **Deactivating is not deleting.** The API soft-deactivates, the row stays, and the same row can
 * be switched back on — so the control says "Deactivate" and the reverse is one press away. A
 * contact that had simply vanished would be retyped from memory, and usually wrongly.
 */
export const BillingContactsPanel = ({ organizationId, onChanged }: BillingContactsPanelProps) => {
  const [contacts, setContacts] = useState<BillingContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  /*
    Item-scoped, not a single boolean. Two rows can be switched on and off independently and a
    shared flag would grey out every button in the list while one of them was in flight.
  */
  const [pendingContactId, setPendingContactId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BillingContact | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setLoadError(null);
      try {
        const loaded = await getBillingContacts(organizationId, signal);
        if (signal?.aborted) return;
        setContacts(loaded);
      } catch (err: unknown) {
        if (signal?.aborted) return;
        setLoadError(describe(err, 'Could not load the billing contacts.'));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [organizationId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setModalError(null);
    setModalOpen(true);
  };

  const openEdit = (contact: BillingContact) => {
    setEditing(contact);
    setModalError(null);
    setModalOpen(true);
  };

  const handleSave = async (draft: BillingContactDraft) => {
    const email = draft.email.trim();
    if (!email) {
      setModalError('Enter the email address invoices should go to.');
      return;
    }

    setSaving(true);
    setModalError(null);
    try {
      const request = {
        email,
        name: draft.name.trim() ? draft.name.trim() : null,
        kind: draft.kind,
        isActive: draft.isActive,
      };
      if (editing) {
        await updateBillingContact(organizationId, editing.id, request);
      } else {
        await createBillingContact(organizationId, request);
      }
      setModalOpen(false);
      await load();
      onChanged();
    } catch (err: unknown) {
      // A 409 here is the API saying this address is already on the organization. Its message is
      // the useful one, so it is shown rather than replaced with a generic failure.
      setModalError(describe(err, 'Could not save the contact.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (contact: BillingContact) => {
    setPendingContactId(contact.id);
    setRowError(null);
    try {
      await deactivateBillingContact(organizationId, contact.id);
      await load();
      onChanged();
    } catch (err: unknown) {
      setRowError(describe(err, 'Could not deactivate the contact.'));
    } finally {
      setPendingContactId(null);
    }
  };

  const handleReactivate = async (contact: BillingContact) => {
    setPendingContactId(contact.id);
    setRowError(null);
    try {
      await updateBillingContact(organizationId, contact.id, {
        email: contact.email,
        name: contact.name,
        kind: contact.kind,
        isActive: true,
      });
      await load();
      onChanged();
    } catch (err: unknown) {
      setRowError(describe(err, 'Could not reactivate the contact.'));
    } finally {
      setPendingContactId(null);
    }
  };

  const activeToCount = contacts.filter((c) => c.isActive && c.kind === 'To').length;

  return (
    <AdminPanel
      title="Billing contacts"
      titleId="billing-contacts-title"
      description={
        <>
          The accounts payable addresses for this organization, not the coaches&apos;. The first
          active one becomes the Stripe customer&apos;s email, which is where Stripe sends the
          invoice.
        </>
      }
      actions={
        <Button variant="secondary" onClick={openAdd} data-testid="billing-contact-add">
          Add contact
        </Button>
      }
    >

      {loadError && (
        <p className="admin-error" role="alert">
          {loadError}
        </p>
      )}

      {rowError && (
        <p className="admin-error" role="alert" data-testid="billing-contact-row-error">
          {rowError}
        </p>
      )}

      {loading ? (
        <p className="admin-state">Loading the contacts...</p>
      ) : contacts.length === 0 ? (
        <p className="admin-state" data-testid="billing-contacts-empty">
          No billing contacts yet. Nothing can be invoiced or chased until there is at least one To
          address.
        </p>
      ) : (
        <>
          {activeToCount === 0 && (
            <p
              className="billing-panel__banner billing-panel__banner--error"
              data-testid="billing-contacts-none-active"
            >
              Every To contact is deactivated, so no reminder can be sent. Reactivate one or add a
              new one.
            </p>
          )}
          <ul className="billing-panel__contact-list">
            {contacts.map((contact) => (
              <li
                key={contact.id}
                className={`billing-panel__contact${
                  contact.isActive ? '' : ' billing-panel__contact--inactive'
                }`}
                data-testid={`billing-contact-${contact.id}`}
              >
                <span className="billing-panel__contact-main">
                  <span className="billing-panel__contact-email">{contact.email}</span>
                  <span className="billing-panel__contact-meta">
                    {contact.name ?? ''}
                    {contact.isActive ? '' : ' - deactivated'}
                  </span>
                </span>
                <span className="billing-panel__row-actions">
                  <Button
                    variant="secondary"
                    onClick={() => openEdit(contact)}
                    disabled={pendingContactId === contact.id}
                    data-testid={`billing-contact-edit-${contact.id}`}
                  >
                    Edit
                  </Button>
                  {contact.isActive ? (
                    <Button
                      variant="secondary"
                      onClick={() => handleDeactivate(contact)}
                      loading={pendingContactId === contact.id}
                      disabled={pendingContactId === contact.id}
                      data-testid={`billing-contact-deactivate-${contact.id}`}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => handleReactivate(contact)}
                      loading={pendingContactId === contact.id}
                      disabled={pendingContactId === contact.id}
                      data-testid={`billing-contact-reactivate-${contact.id}`}
                    >
                      Reactivate
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <BillingContactModal
        isOpen={modalOpen}
        contact={editing}
        saving={saving}
        error={modalError}
        onSave={handleSave}
        onClose={() => setModalOpen(false)}
      />
    </AdminPanel>
  );
};
